import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const { signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState<string>('');
  const [pendingAccountEmail, setPendingAccountEmail] = useState<string>('');

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setAuthErrorMessage('');
    try {
      await signInWithGoogle();
    } catch (error: any) {
      console.error('Sign in failed:', error);
      const code = String(error?.code || '');
      const attemptedEmail = String(error?.customData?.email || '').trim();
      if (code === 'auth/user-disabled') {
        setPendingAccountEmail(attemptedEmail);
        setAuthErrorMessage(
          'This account is pending admin enablement. Please ask an admin to enable your user in Firebase Authentication.'
        );
      } else {
        setPendingAccountEmail('');
        setAuthErrorMessage('Sign in failed. Please try again or contact support.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ts-cream flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-10">

        {/* Wordmark + tagline */}
        <div className="text-center">
          <h1 className="font-serif text-5xl font-semibold tracking-wide mb-3">
            <span className="text-ts-charcoal">Top</span><span className="text-ts-gold">Seeds</span>
          </h1>
          <p className="text-sm uppercase tracking-widest text-ts-gold font-medium">
            Executive Career Intelligence
          </p>
        </div>

        {/* Sign-in card */}
        <div className="bg-white rounded-xl shadow-sm border border-ts-border p-8">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-serif text-2xl font-semibold text-ts-charcoal mb-1">
                Welcome back
              </h2>
              <p className="text-sm text-gray-500">
                Sign in with your Google account to continue
              </p>
            </div>

            {authErrorMessage && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                <p className="text-sm font-medium text-amber-800">
                  {authErrorMessage}
                </p>
                {pendingAccountEmail && (
                  <p className="text-xs mt-1 text-amber-700 break-all">
                    Attempted sign-in: {pendingAccountEmail}
                  </p>
                )}
              </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center px-4 py-3 border border-ts-border rounded-lg shadow-sm bg-white text-ts-charcoal hover:bg-ts-cream focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ts-gold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ts-gold mr-3"></div>
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </div>
              )}
            </button>

            <div className="text-center text-xs text-gray-400">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-400">
            Secure authentication powered by Firebase
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;